import type { ReactNode } from "react";

type Props = {
  /**
   * Status / celebration / save-message / error banners. Rendered
   * full-width above the grid so transient feedback is never
   * sandboxed inside a column.
   */
  banners?: ReactNode;
  /**
   * Left column at `lg:+`. Ingestion variant: the manual ingestion
   * box. Suggestion variant: the engagement stats panel + the
   * "Browse indexed posts" explainer paragraph.
   */
  leftColumn: ReactNode;
  /**
   * Right column at `lg:+`. Always the indexed posts region
   * (toolbar · pagination · cards list). `AdsAssistantAggregationSection`
   * also auto-opens the inner `<details>` at this breakpoint so the
   * list doesn't sit collapsed inside the column it's supposed to
   * fill — see the matchMedia effect there.
   */
  rightColumn: ReactNode;
};

/**
 * Ads Assistant — Ingestion + Suggestion desktop layout. Mobile
 * (`<lg`) keeps the legacy vertical stack (banners → left → right)
 * with `space-y-5` so the indexed-posts `<details>` collapse still
 * earns its keep — on a phone you don't want 100 cards pushing the
 * form way down.
 *
 * Desktop (`lg:+`) splits the workspace into a 9/3 column grid: manual
 * ingestion / stats get ~75% width; indexed posts stay in a compact
 * collapsible pane (dev-sync 5c60f6c7).
 *
 * No sticky on either column: the manual form is tall (15+ fields
 * with categories / post tags / template picker / slot fill /
 * quality fieldset) and would clip awkwardly when pinned, and the
 * posts pane paginates so there's no infinite-scroll concern that
 * would benefit from a pinned form/stats anchor. Both columns
 * scroll with the page naturally.
 *
 * Layout extracted into its own component to keep
 * `AdsAssistantAggregationSection.tsx` under the 300-line hard
 * threshold from `.cursor/rules/file-size-hygiene.mdc`.
 */
export function AdsAssistantAggregationDesktopLayout(props: Props) {
  return (
    <div className="space-y-5">
      {props.banners}
      <div className="space-y-5 lg:grid lg:grid-cols-12 lg:gap-6 lg:space-y-0 lg:items-start">
        <div className="space-y-5 lg:col-span-9">{props.leftColumn}</div>
        <div className="space-y-5 lg:col-span-3">{props.rightColumn}</div>
      </div>
    </div>
  );
}
